"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import ReviewRequestPrompt, {
  checkReviewSuppression,
  recordReviewSuppression,
  checkHappyScore
} from '@/components/review/ReviewRequestPrompt';

export type ReviewTriggerMoment =
  | 'goal_achieved'
  | 'upgraded_to_paid'
  | 'first_scenario_completed'
  | 'net_worth_milestone';

interface ReviewRequestContextValue {
  triggerReviewRequest: (moment: ReviewTriggerMoment) => void;
  isPromptOpen: boolean;
  closePrompt: () => void;
}

const ReviewRequestContext = createContext<ReviewRequestContextValue | null>(null);

export function useReviewRequestTrigger() {
  const context = useContext(ReviewRequestContext);
  if (!context) {
    throw new Error('useReviewRequestTrigger must be used within ReviewRequestTriggerProvider');
  }
  return context;
}

interface ReviewRequestTriggerProviderProps {
  children: React.ReactNode;
  triggerMoments?: ReviewTriggerMoment[];
}

export function ReviewRequestTriggerProvider({
  children,
  triggerMoments = ['goal_achieved', 'upgraded_to_paid', 'first_scenario_completed', 'net_worth_milestone']
}: ReviewRequestTriggerProviderProps) {
  const { user } = useAuth();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const pendingTriggerRef = useRef<ReviewTriggerMoment | null>(null);
  const suppressionCheckedRef = useRef(false);

  const canShowPrompt = useCallback(async (): Promise<boolean> => {
    if (!user?.id) return false;

    try {
      const [suppressionResult, happyScoreResult] = await Promise.all([
        checkReviewSuppression(user.id),
        checkHappyScore()
      ]);

      if (suppressionResult.isSuppressed) {
        return false;
      }

      if (!happyScoreResult.meetsThreshold) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking prompt eligibility:', error);
      return false;
    }
  }, [user?.id]);

  const triggerReviewRequest = useCallback(async (moment: ReviewTriggerMoment) => {
    if (!triggerMoments.includes(moment)) return;
    if (!user?.id) return;

    pendingTriggerRef.current = moment;

    const canShow = await canShowPrompt();
    if (!canShow) return;

    setTimeout(() => {
      if (pendingTriggerRef.current === moment) {
        setIsPromptOpen(true);
      }
    }, 2000);
  }, [triggerMoments, canShowPrompt, user?.id]);

  const closePrompt = useCallback(() => {
    setIsPromptOpen(false);
    pendingTriggerRef.current = null;
  }, []);

  const handleDismiss = useCallback(async (suppressDays: number) => {
    if (!user?.id) return;

    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + suppressDays);
      await recordReviewSuppression(user.id, 'in_app_prompt', expiresAt);
    } catch (error) {
      console.error('Error recording suppression:', error);
    }

    closePrompt();
  }, [user?.id, closePrompt]);

  useEffect(() => {
    if (user?.id && !suppressionCheckedRef.current) {
      suppressionCheckedRef.current = true;
    }
  }, [user?.id]);

  return (
    <ReviewRequestContext.Provider value={{ triggerReviewRequest, isPromptOpen, closePrompt }}>
      {children}
      <ReviewRequestPrompt
        isOpen={isPromptOpen}
        onClose={closePrompt}
        onDismiss={handleDismiss}
      />
    </ReviewRequestContext.Provider>
  );
}

export async function triggerReviewMoment(moment: ReviewTriggerMoment): Promise<void> {
  const event = new CustomEvent('review-trigger', { detail: { moment } });
  window.dispatchEvent(event);
}